// firebaseService.js
// Service for syncing fantasy golf league data with Firebase Firestore

import { initializeApp } from 'firebase/app';
import { 
  getFirestore, 
  doc, 
  setDoc, 
  getDoc,
  onSnapshot 
} from 'firebase/firestore';

// Firebase configuration
const firebaseConfig = {
  apiKey: "AIzaSyDtZ9RrXjyVTW_9pwH7oT6HL74JJv-LiUU",
  authDomain: "swimming-pool-2026.firebaseapp.com",
  projectId: "swimming-pool-2026",
  storageBucket: "swimming-pool-2026.firebasestorage.app",
  messagingSenderId: "5369141127",
  appId: "1:5369141127:web:76bfbcff055a3d9bb26522",
  measurementId: "G-N7Z98V800F"
};

// Initialize Firebase
const app = initializeApp(firebaseConfig);
const db = getFirestore(app);

class FirebaseService {

  // ── Participants ───────────────────────────────────────────────

  async saveParticipants(participants) {
    try {
      await setDoc(doc(db, 'league', 'participants'), {
        data: participants,
        lastUpdated: new Date().toISOString()
      });
      console.log('Participants saved to Firebase');
    } catch (error) {
      console.error('Error saving participants:', error);
      throw error;
    }
  }

  async loadParticipants() {
    try {
      const docRef = doc(db, 'league', 'participants');
      const docSnap = await getDoc(docRef);
      if (docSnap.exists()) {
        console.log('Participants loaded from Firebase');
        return docSnap.data().data;
      } else {
        console.log('No participants data found in Firebase');
        return null;
      }
    } catch (error) {
      console.error('Error loading participants:', error);
      throw error;
    }
  }

  subscribeToParticipants(callback) {
    const docRef = doc(db, 'league', 'participants');
    return onSnapshot(docRef, (doc) => {
      if (doc.exists()) {
        console.log('Participants updated in real-time');
        callback(doc.data().data);
      }
    });
  }

  // ── Golfers ────────────────────────────────────────────────────

  async saveGolfers(golfers) {
    try {
      await setDoc(doc(db, 'league', 'golfers'), {
        data: golfers,
        lastUpdated: new Date().toISOString()
      });
      console.log('Golfers saved to Firebase');
    } catch (error) {
      console.error('Error saving golfers:', error);
      throw error;
    }
  }

  async loadGolfers() {
    try {
      const docRef = doc(db, 'league', 'golfers');
      const docSnap = await getDoc(docRef);
      if (docSnap.exists()) {
        console.log('Golfers loaded from Firebase');
        return docSnap.data().data;
      } else {
        console.log('No golfers data found in Firebase');
        return null;
      }
    } catch (error) {
      console.error('Error loading golfers:', error);
      throw error;
    }
  }

  subscribeToGolfers(callback) {
    const docRef = doc(db, 'league', 'golfers');
    return onSnapshot(docRef, (doc) => {
      if (doc.exists()) {
        console.log('Golfers updated in real-time');
        callback(doc.data().data);
      }
    });
  }

  // ── Tournament History (NEW) ───────────────────────────────────

  /**
   * Save full tournament history to Firebase
   * Shape: { [tournamentId]: { [participantName]: { earnings, lineup, isMajor } } }
   */
  async saveTournamentHistory(tournamentHistory) {
    try {
      await setDoc(doc(db, 'league', 'tournamentHistory'), {
        data: tournamentHistory,
        lastUpdated: new Date().toISOString()
      });
      console.log('Tournament history saved to Firebase');
    } catch (error) {
      console.error('Error saving tournament history:', error);
      throw error;
    }
  }

  /**
   * Load tournament history from Firebase
   */
  async loadTournamentHistory() {
    try {
      const docRef = doc(db, 'league', 'tournamentHistory');
      const docSnap = await getDoc(docRef);
      if (docSnap.exists()) {
        console.log('Tournament history loaded from Firebase');
        return docSnap.data().data;
      } else {
        console.log('No tournament history found in Firebase');
        return {};
      }
    } catch (error) {
      console.error('Error loading tournament history:', error);
      return {};
    }
  }

  /**
   * Save results for a single completed tournament
   */
  async saveSingleTournamentResult(tournamentId, results) {
    try {
      const history = await this.loadTournamentHistory();
      history[tournamentId] = results;
      await this.saveTournamentHistory(history);
      console.log(`Results for tournament ${tournamentId} locked into history`);
    } catch (error) {
      console.error('Error saving single tournament result:', error);
      throw error;
    }
  }

  // ── Bulk Operations ────────────────────────────────────────────

  async saveAllData(data) {
    try {
      const promises = [
        this.saveParticipants(data.participants),
        this.saveGolfers(data.golfers)
      ];
      if (data.tournamentHistory) {
        promises.push(this.saveTournamentHistory(data.tournamentHistory));
      }
      await Promise.all(promises);
      console.log('All league data saved to Firebase');
    } catch (error) {
      console.error('Error saving all data:', error);
      throw error;
    }
  }

  async loadAllData() {
    try {
      const [participants, golfers, tournamentHistory] = await Promise.all([
        this.loadParticipants(),
        this.loadGolfers(),
        this.loadTournamentHistory()
      ]);
      return { participants, golfers, tournamentHistory };
    } catch (error) {
      console.error('Error loading all data:', error);
      throw error;
    }
  }
}

// Create singleton instance
const firebaseService = new FirebaseService();
export default firebaseService;
